import React, { useEffect } from 'react'
import { t } from 'ttag'
import { useForm, Controller } from 'react-hook-form'
import { Box, FormGroup, InputAdornment, ListItem, ListItemText, ListItemIcon, Typography } from '@material-ui/core'
import YumButton from '../../components/YumButton/YumButton'
import YumInput from '../../components/YumInput/YumInput'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumUploadWrapper from '../../components/YumUpload/YumUploadWrapper'
import { FileDto, GetCompanyInfo$Response, UserCompanyDto } from '../../services/api.types'
import { getContractPeriod } from '../../common/helpers/challenges'
import { scrollToTop } from '../../common/helpers/scroll'
import { hasCompanyFeatureFlag } from '../../common/helpers/featureFlags'

// --- Form data shape ---
interface CompanyInfoFormData {
  name: string
  logoUrl: string
  hideStatisticsView: boolean
  hideRankingView: boolean
  userGoalsEnabled: boolean
  autoJoinEnabled: boolean
  reportRequired: boolean
  reportRequiredForUsers: boolean
  passwordMinLength: number | null
  customPolicyUrl: string
  emailVerificationRequired: boolean
}

// --- Props ---
export interface ICompanyAdminProps {
  managedCompanies: UserCompanyDto[]
  userCompanies: UserCompanyDto[]
  companyInfo: GetCompanyInfo$Response | null
  loading: boolean
  saving: boolean
  lastUpdated: number | null
  selectedCompanyId: string | null
}

export interface ICompanyAdminDispatchProps {
  getCompanyInfo: (payload: { companyId: string }) => void
  updateCompanyInfo: (payload: any) => void
  setCompanyAdminSelectedCompanyId: (payload: string) => void
}

type CompanyAdminProps = ICompanyAdminProps & ICompanyAdminDispatchProps

export default function CompanyAdmin(props: CompanyAdminProps) {
  const [replaceYumuuvLogo, setReplaceYumuuvLogo] = React.useState(false)

  const {
    managedCompanies,
    userCompanies,
    companyInfo,
    loading,
    saving,
    lastUpdated,
    selectedCompanyId,
    getCompanyInfo,
    updateCompanyInfo,
    setCompanyAdminSelectedCompanyId,
  } = props

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  const { control, handleSubmit, reset, watch, setValue } = useForm<CompanyInfoFormData>({
    defaultValues: {
      name: '',
      logoUrl: '',
      hideStatisticsView: false,
      hideRankingView: false,
      userGoalsEnabled: false,
      autoJoinEnabled: false,
      reportRequired: false,
      reportRequiredForUsers: false,
      passwordMinLength: null,
      customPolicyUrl: '',
      emailVerificationRequired: false,
    },
  })

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setCompanyAdminSelectedCompanyId(companyId)
    }
  }, [])

  // Load company info whenever selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanyInfo({ companyId })
    }
  }, [companyId])

  // Populate form when company info loads
  useEffect(() => {
    if (companyInfo) {
      setReplaceYumuuvLogo(!!companyInfo.brandLogoUrl)
      reset({
        name: companyInfo.name || '',
        logoUrl: companyInfo.logoUrl || companyInfo.brandLogoUrl || '',
        hideStatisticsView: companyInfo.hideStatisticsView || false,
        hideRankingView: companyInfo.hideRankingView || false,
        userGoalsEnabled: companyInfo.userGoalsEnabled || false,
        autoJoinEnabled: companyInfo.autoJoinEnabled || false,
        reportRequired: companyInfo.reportRequired || false,
        reportRequiredForUsers: companyInfo.reportRequiredForUsers || false,
        passwordMinLength: companyInfo.passwordMinLength || null,
        customPolicyUrl: companyInfo.customPolicyUrl || '',
        emailVerificationRequired: companyInfo.emailVerificationRequired || false,
      })
    } else {
      reset({
        name: '',
        logoUrl: '',
        hideStatisticsView: false,
        hideRankingView: false,
        userGoalsEnabled: false,
        autoJoinEnabled: false,
        reportRequired: false,
        reportRequiredForUsers: false,
        passwordMinLength: null,
        customPolicyUrl: '',
        emailVerificationRequired: false,
      })
    }
  }, [companyInfo, lastUpdated])

  // Scroll to top after successful save
  const prevSavingRef = React.useRef(saving)
  useEffect(() => {
    if (prevSavingRef.current && !saving) {
      scrollToTop()
    }
    prevSavingRef.current = saving
  }, [saving])

  // Handle company selection change
  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setCompanyAdminSelectedCompanyId(newCompanyId)
    getCompanyInfo({ companyId: newCompanyId })
  }

  // Handle form submit — preserve countries/countryRequired from current companyInfo
  const onSubmit = (data: CompanyInfoFormData) => {
    if (!companyId || !companyInfo) return
    updateCompanyInfo({
      companyId,
      ...data,
      brandLogoUrl: replaceYumuuvLogo ? data.logoUrl : '',
      countries: companyInfo.countries || '[]',
      countryRequired: companyInfo.countryRequired || false,
    })
  }

  // Build a FileDto from the logoUrl for YumUploadWrapper
  const logoUrl = watch('logoUrl')
  const existingImageDto: FileDto | null = logoUrl ? { id: '' as any, thumbnail: logoUrl, full: logoUrl } : null

  const handleUploadedImageDto = (fileDto: FileDto | null) => {
    setValue('logoUrl', fileDto?.full || '')
  }

  // Helper to render a section title
  const renderSectionTitle = (title: string) => (
    <Typography variant="h2" style={{ marginTop: 24, marginBottom: 12 }}>
      {title}
    </Typography>
  )

  // Helper to render a toggle switch row (inverted: UI shows opposite of stored value)
  const renderSwitch = (name: keyof CompanyInfoFormData, label: string, options?: { inverted?: boolean }) => (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, ref, ...field } }) => {
        const checked = options?.inverted ? !value : !!value
        return (
          <ListItem
            role={undefined}
            dense
            button
            style={{ paddingLeft: 0 }}
            onClick={() => setValue(name, !value as any)}
          >
            <ListItemText primary={label} />
            <ListItemIcon style={{ minWidth: 'auto' }}>
              <YumSwitch {...field} checked={checked} onChange={() => setValue(name, !value as any)} inputRef={ref} />
            </ListItemIcon>
          </ListItem>
        )
      }}
    />
  )

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>{t`General`}</h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>

      {companyId && getContractPeriod(managedCompanies, companyId) && (
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          {getContractPeriod(managedCompanies, companyId)}
        </Typography>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* --- General --- */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <YumInput
              label={t`Organization name`}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              style={{ marginBottom: 12 }}
            />
          )}
        />

        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
          {t`Logo`}
        </Typography>
        {companyId && (
          <YumUploadWrapper
            companyId={companyId as any}
            existingImageDto={existingImageDto}
            updateUploadedImageDto={handleUploadedImageDto}
          />
        )}

        {logoUrl &&
          (hasCompanyFeatureFlag(userCompanies, companyId, 'replaceBrandLogo') || !!companyInfo?.brandLogoUrl) && (
            <ListItem
              role={undefined}
              dense
              button
              style={{ paddingLeft: 0 }}
              onClick={() => setReplaceYumuuvLogo(!replaceYumuuvLogo)}
            >
              <ListItemText primary={t`Replace YuMuuv brand logo`} />
              <ListItemIcon style={{ minWidth: 'auto' }}>
                <YumSwitch checked={replaceYumuuvLogo} onChange={() => setReplaceYumuuvLogo(!replaceYumuuvLogo)} />
              </ListItemIcon>
            </ListItem>
          )}

        {/* --- Onboarding --- */}
        {renderSectionTitle(t`Onboarding`)}

        <Controller
          name="passwordMinLength"
          control={control}
          render={({ field }) => (
            <YumInput
              label={t`Minimum password length`}
              value={field.value ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value
                field.onChange(val === '' ? null : parseInt(val, 10))
              }}
              onBlur={field.onBlur}
              type="number"
              style={{ marginBottom: 12 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end">
                    {t`characters`}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="customPolicyUrl"
          control={control}
          render={({ field }) => (
            <YumInput
              label={t`Custom policy URL`}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="https://"
              style={{ marginBottom: 12 }}
            />
          )}
        />

        {/* --- Challenges --- */}
        {renderSectionTitle(t`Challenges`)}
        <FormGroup>
          {renderSwitch('userGoalsEnabled', t`Enable user-created challenges`)}
          {renderSwitch('autoJoinEnabled', t`Auto-join enabled by default`)}
        </FormGroup>

        {/* --- Statistics view --- */}
        {renderSectionTitle(t`Statistics view`)}
        <FormGroup>
          {renderSwitch('reportRequired', t`Enable statistics`)}
          {renderSwitch('reportRequiredForUsers', t`Show "Statistics"`)}
          {renderSwitch('hideStatisticsView', t`Show "Fun Facts"`, { inverted: true })}
          {renderSwitch('hideRankingView', t`Show "TOP 100"`, { inverted: true })}
        </FormGroup>

        {/* --- Security --- (gated by the EMAIL_VERIFICATION feature flag) */}
        {hasCompanyFeatureFlag(userCompanies, companyId, 'emailVerification') && (
          <>
            {renderSectionTitle(t`Security`)}
            <FormGroup>
              {renderSwitch('emailVerificationRequired', t`Require email verification code on login`)}
            </FormGroup>
          </>
        )}

        {/* --- Save Button --- */}
        <Box style={{ marginTop: 24 }}>
          <YumButton type="submit" showLoader={true} disabled={saving || !companyId}>
            {t`Save changes`}
          </YumButton>
        </Box>
      </form>
    </Card>
  )
}
